import {useState} from 'react';
import {createMutationErrorHandler, trpc} from '@common-web/lib/trpc';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {Alert, AlertDescription, AlertTitle} from '@common-web/components/ui/alert';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {toast} from '@common-web/hooks/use-toast';
import {KeyRound, Loader2, Copy, Check, AlertTriangle} from 'lucide-react';

type ExternalUserType = 'COMMISSIONS' | 'BEEP';

type CreatedExternalUser = {
  id: string;
  name: string;
  type: string;
  clientName: string;
  token: string;
};

const EXTERNAL_USER_TYPES: {value: ExternalUserType; label: string; description: string}[] = [
  {
    value: 'BEEP',
    label: 'BEEP — Member Disenroll',
    description: 'Disenroll Synrgy members from VBA coverage (must be scoped to the Synrgy client).',
  },
  {
    value: 'COMMISSIONS',
    label: 'COMMISSIONS — Commissions API',
    description: 'Read/write commission agents and group assignments for the selected client.',
  },
];

export function ExternalUserForm() {
  const [form, setForm] = useState<{clientId: string; name: string; type: ExternalUserType}>({
    clientId: '',
    name: '',
    type: 'BEEP',
  });
  const [created, setCreated] = useState<CreatedExternalUser | null>(null);
  const [copied, setCopied] = useState(false);

  const {data: clients, isLoading: clientsIsLoading} =
    trpc.internal.userManagement.getClients.useQuery();

  const createExternalUser = trpc.internal.userManagement.createExternalUser.useMutation({
    onSuccess: (data) => {
      setCreated(data.externalUser);
      toast({
        title: 'External user created',
        description: `An API token has been generated for ${data.externalUser.name}.`,
      });
    },
    onError: createMutationErrorHandler('Error creating external user'),
  });

  const handleSubmit = () => {
    createExternalUser.mutate(form);
  };

  const handleCopy = async () => {
    if (!created) return;
    await navigator.clipboard.writeText(created.token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCreateAnother = () => {
    setCreated(null);
    setCopied(false);
    setForm({clientId: '', name: '', type: 'BEEP'});
  };

  if (created) {
    return (
      <Card className='border-gray-200 shadow-sm'>
        <CardHeader className='bg-gray-50 border-b'>
          <CardTitle className='text-lg font-semibold text-brand-navy'>
            External API User Created
          </CardTitle>
        </CardHeader>
        <CardContent className='p-6 space-y-6'>
          <div className='grid grid-cols-3 gap-4'>
            <div className='space-y-1'>
              <div className='text-sm text-brand-gray'>Name</div>
              <div className='font-medium text-brand-navy'>{created.name}</div>
            </div>
            <div className='space-y-1'>
              <div className='text-sm text-brand-gray'>Type</div>
              <div className='font-medium text-brand-navy'>{created.type}</div>
            </div>
            <div className='space-y-1'>
              <div className='text-sm text-brand-gray'>Client</div>
              <div className='font-medium text-brand-navy'>{created.clientName}</div>
            </div>
          </div>

          <div className='space-y-2'>
            <Label>API Token</Label>
            <div className='flex items-center gap-2'>
              <code className='flex-1 break-all rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-sm text-brand-navy'>
                {created.token}
              </code>
              <Button
                type='button'
                variant='outline'
                onClick={handleCopy}
                className='shrink-0'
                aria-label='Copy token'
              >
                {copied ? <Check className='h-4 w-4' /> : <Copy className='h-4 w-4' />}
              </Button>
            </div>
          </div>

          <Alert variant='destructive'>
            <AlertTriangle className='h-4 w-4' />
            <AlertTitle>Copy this token now</AlertTitle>
            <AlertDescription>
              This is the only time the token will be shown. Store it in a secrets manager and share
              it with the partner over a secure channel. They authenticate by sending it as{' '}
              <code className='font-mono'>Authorization: Bearer &lt;token&gt;</code>. To revoke,
              deactivate or delete this external user.
            </AlertDescription>
          </Alert>

          <Button
            className='bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold w-full'
            onClick={handleCreateAnother}
          >
            Create Another External User
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className='border-gray-200 shadow-sm'>
      <CardHeader className='bg-gray-50 border-b'>
        <CardTitle className='text-lg font-semibold text-brand-navy'>
          External API User
        </CardTitle>
      </CardHeader>
      <CardContent className='p-6 space-y-6'>
        <div className='space-y-2'>
          <Label htmlFor='external-name'>Name</Label>
          <Input
            id='external-name'
            placeholder='e.g. BEEP Member Disenroll'
            value={form.name}
            onChange={(e) => setForm({...form, name: e.target.value})}
            className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
          />
          <p className='text-sm text-brand-gray'>
            A label to identify this integration. Not shown to the partner.
          </p>
        </div>

        <div className='space-y-2'>
          <Label htmlFor='external-client'>Client</Label>
          {clientsIsLoading ? (
            <Skeleton className='h-10 w-full' />
          ) : (
            <Select
              value={form.clientId}
              onValueChange={(value) => setForm({...form, clientId: value})}
            >
              <SelectTrigger className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'>
                <SelectValue placeholder='Select a client' />
              </SelectTrigger>
              <SelectContent>
                {clients?.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className='space-y-2'>
          <Label htmlFor='external-type'>API Type</Label>
          <Select
            value={form.type}
            onValueChange={(value) => setForm({...form, type: value as ExternalUserType})}
          >
            <SelectTrigger className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EXTERNAL_USER_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className='text-sm text-brand-gray'>
            {EXTERNAL_USER_TYPES.find((t) => t.value === form.type)?.description}
          </p>
        </div>

        {form.type === 'BEEP' && (
          <Alert>
            <AlertTriangle className='h-4 w-4' />
            <AlertTitle>BEEP tokens must be scoped to Synrgy</AlertTitle>
            <AlertDescription>
              The disenroll endpoint rejects any BEEP token whose client is not Synrgy. Make sure
              you select the Synrgy client above.
            </AlertDescription>
          </Alert>
        )}

        <Button
          className='bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold w-full'
          onClick={handleSubmit}
          disabled={!form.name || !form.clientId || createExternalUser.isPending}
        >
          {createExternalUser.isPending ? (
            <Loader2 className='h-4 w-4 mr-2 animate-spin' />
          ) : (
            <KeyRound className='h-4 w-4 mr-2' />
          )}
          Generate API Token
        </Button>
      </CardContent>
    </Card>
  );
}
